import { fetch_json, is_email } from '#lib/util.js';
import {
  MAX_EVENT_REGISTRATION_NAME_LENGTH,
  MAX_EVENT_REGISTRATION_EMAIL_LENGTH,
  MAX_EVENT_REGISTRATION_NOTES_LENGTH,
} from '#lib/app_config.js';

/**
 * The attendee validity rule, shared by the registration form, the member
 * card and the guest list: name required, e-mail optional but must be valid,
 * both capped at the server's field lengths. Returns translation keys per
 * invalid field; an empty object means the fields are valid.
 *
 * @param {{name: string, email: string}} fields
 * @param {object} [options]
 * @param {boolean} [options.email_required]
 * @param {boolean} [options.report_empty] - Empty required fields are only
 *   reported on submit, so leaving a field untouched stays quiet
 * @returns {{name?: string, email?: string}}
 */
export function get_attendee_errors({ name, email, notes }, { email_required = false, report_empty = true } = {}) {
  const errors = {};
  const trimmed_name = (name ?? '').trim();
  const trimmed_email = (email ?? '').trim();
  if (report_empty && !trimmed_name) errors.name = 'name_required';
  if (trimmed_name.length > MAX_EVENT_REGISTRATION_NAME_LENGTH) errors.name = 'name_too_long';
  if ((trimmed_email && !is_email(trimmed_email)) || (report_empty && !trimmed_email && email_required)) {
    errors.email = 'email_invalid';
  }
  if (trimmed_email.length > MAX_EVENT_REGISTRATION_EMAIL_LENGTH) errors.email = 'email_too_long';
  if (typeof notes === 'string' && notes.trim().length > MAX_EVENT_REGISTRATION_NOTES_LENGTH) errors.notes = 'notes_too_long';
  return errors;
}

/** The same errors as the messages a person reads: one translation step for every caller */
export function translate_attendee_errors(errors, t) {
  return Object.fromEntries(Object.entries(errors).map(([field, key]) => [field, t(key)]));
}

/**
 * Sends registration changes as intent-shaped requests: cancellations first
 * (they free seats), then edits, then new guests. Stops at the first failure.
 * On paid events the add request answers with a Stripe Checkout URL; the
 * caller must redirect there to complete the held seats.
 *
 * @param {number} event_entry_id
 * @param {object} changes
 * @param {number[]} [changes.cancelled_ids]
 * @param {Array<{event_registration_id: number, name: string, email: string, notes?: string}>} [changes.changed]
 * @param {Array<{name: string, email: string, notes?: string}>} [changes.added]
 * @param {boolean} [changes.invoice_requested] - paid events: the member wants a Stripe invoice for the added seats
 * @returns {Promise<{success: boolean, error?: string, checkout_url?: string, registrations?: object[]}>}
 */
export async function submit_registration_changes(event_entry_id, { cancelled_ids = [], changed = [], added = [], invoice_requested = false }) {
  const requests = [];
  if (cancelled_ids.length > 0) {
    requests.push(['/api/cancel-event-registrations', { event_entry_id, event_registration_ids: cancelled_ids }]);
  }
  if (changed.length > 0) {
    requests.push(['/api/update-event-registrations', { event_entry_id, attendees: changed }]);
  }
  if (added.length > 0) {
    requests.push(['/api/add-event-registrations', { event_entry_id, attendees: added, invoice_requested }]);
  }

  let registrations = [];
  try {
    for (const [endpoint, payload] of requests) {
      const result = await fetch_json('POST', endpoint, payload);
      if (!result?.success) {
        return { success: false, error: result?.error || 'event_registrations_update_error' };
      }
      if (Array.isArray(result.registrations)) registrations = result.registrations;
      if (result.checkout_url) {
        return { success: true, checkout_url: result.checkout_url, registrations };
      }
    }
  } catch (error) {
    console.error(error);
    return { success: false, error: 'event_registrations_update_error' };
  }
  return { success: true, registrations };
}

/** Records or clears an offline payment on one registration (admin only) */
export async function record_registration_payment(event_entry_id, payload) {
  try {
    const result = await fetch_json('POST', '/api/record-event-registration-payment', { event_entry_id, ...payload });
    if (!result?.success) {
      return { success: false, error: result?.error || 'event_registrations_update_error' };
    }
    return result;
  } catch (error) {
    console.error(error);
    return { success: false, error: 'event_registrations_update_error' };
  }
}

/** Translation key for the toast after a successful member change */
export function get_registration_toast_key({ added = 0, changed = 0, cancelled = 0 }) {
  const type = cancelled === 0 && changed === 0 ? 'created'
    : added === 0 && changed === 0 ? 'cancelled'
    : 'updated';
  const plural = added + changed + cancelled !== 1;
  return `registration_toast_${type}_${plural ? 'plural' : 'singular'}`;
}
